import React, { useState, useEffect } from 'react';
import { GoalFramework, GoalPhase, GoalMilestone } from '../../types/DataTypes';
import { storage } from '../../services/storage';
import { API_BASE_URL } from '../../utils/api';

// 🆕 MAR 2026: Research application data fetched from API
interface ResearchAppSource {
  source_id: string;
  application_summary: string;
  key_influence: string;
  cited_finding: string;
  applied_to: string;
  context: string;
  source_metadata?: {
    title: string;
    authors: string[];
    journal: string;
    year: number;
  };
}

interface FrameworkDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  framework: GoalFramework;
  goalTitle: string;
  goalId?: string; // 🆕 MAR 2026: Used to fetch research applications
  initialPhaseIndex?: number; // 🆕 JAN 2025: Support per-phase drilldown
}

export const FrameworkDetailsModal: React.FC<FrameworkDetailsModalProps> = ({
  isOpen,
  onClose,
  framework,
  goalTitle,
  goalId,
  initialPhaseIndex
}) => {
  // 🆕 JAN 2025: Expandable phase state (accordion-style)
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set());

  // 🆕 MAR 2026: Research applications state
  const [researchApps, setResearchApps] = useState<ResearchAppSource[]>([]);
  const [researchFrameworkNames, setResearchFrameworkNames] = useState<string[]>([]);
  const [researchLoading, setResearchLoading] = useState(false);
  
  // Auto-expand initial phase or current phase on open
  useEffect(() => {
    if (isOpen) {
      const phaseToExpand = initialPhaseIndex !== undefined ? initialPhaseIndex : framework.currentPhase;
      setExpandedPhases(new Set([phaseToExpand]));
    }
  }, [isOpen, initialPhaseIndex, framework.currentPhase]);

  // 🆕 MAR 2026: Fetch research applications for the framework
  useEffect(() => {
    if (!isOpen || !goalId) return;
    let cancelled = false;
    const fetchResearch = async () => {
      setResearchLoading(true);
      try {
        const token = await storage.getItem('jwt_token');
        const response = await fetch(`${API_BASE_URL}/api/goals/${goalId}/research-applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!cancelled && response.ok) {
          const data = await response.json();
          // Filter to only framework_generation context apps
          const allApps: ResearchAppSource[] = data.research_applications || [];
          const frameworkApps = allApps.filter(app => app.context === 'framework_generation');
          setResearchApps(frameworkApps.length > 0 ? frameworkApps : allApps);
          // Resource framework names from the same endpoint
          setResearchFrameworkNames(data.resource_framework_names || []);
        }
      } catch {
        // Non-critical, fail silently
      } finally {
        if (!cancelled) setResearchLoading(false);
      }
    };
    fetchResearch();
    return () => { cancelled = true; };
  }, [isOpen, goalId]);
  
  const togglePhase = (index: number) => {
    const newExpanded = new Set(expandedPhases);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedPhases(newExpanded);
  };
  
  if (!isOpen) return null;

  // 🔧 BUG FIX (Nov 18, 2025): Add defensive checks for framework data
  // Frontend was crashing with "Cannot read properties of undefined (reading 'map')"
  // This happens when framework data is missing or incomplete
  if (!framework || !framework.phases || !Array.isArray(framework.phases) || framework.phases.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Planning Framework</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Framework data is not available for this goal.</p>
              <p className="text-sm text-gray-500">The framework may still be generating. Please try again in a moment.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentPhase = framework.phases[framework.currentPhase || 0];
  const completedPhases = framework.currentPhase || 0;
  const totalPhases = framework.phases.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Planning Framework</h2>
            <p className="text-sm text-gray-600 mt-1">{goalTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Framework Overview */}
          <div className="p-6 border-b border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border-2 border-[var(--cedar-primary)] bg-[var(--cedar-subtle)]">
                <h3 className="font-medium text-[var(--cedar-dark)] mb-1">Duration</h3>
                <p className="text-xl font-semibold text-[var(--cedar-dark)]">{framework.totalDurationWeeks} weeks</p>
              </div>
              <div className="p-4 rounded-lg border-2 border-[var(--cedar-primary)] bg-[var(--cedar-subtle)]">
                <h3 className="font-medium text-[var(--cedar-dark)] mb-1">Current Phase</h3>
                <p className="text-sm font-medium text-[var(--cedar-primary)]">
                  {currentPhase?.title || `Phase ${completedPhases + 1}`} ({completedPhases + 1}/{totalPhases})
                </p>
              </div>
            </div>
            
            {/* 🆕 JAN 19, 2026: Methodology Summary */}
            {framework.methodology_summary && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Training Approach</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {framework.methodology_summary}
                </p>
              </div>
            )}
            
            {/* 🆕 JAN 19, 2026: Key Focus Areas */}
            {framework.key_focus_areas && framework.key_focus_areas.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Framework Priorities</h4>
                <div className="flex flex-wrap gap-2">
                  {framework.key_focus_areas.map((area: string, idx: number) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-white border border-[var(--cedar-primary)] rounded-full text-xs font-medium text-[var(--cedar-primary)]"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* 🆕 JAN 19, 2026: Research Context Summary */}
            {framework.research_context_summary && framework.research_context_summary.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">How Research Shaped This Framework</h4>
                <ul className="space-y-1">
                  {framework.research_context_summary.map((bullet: string, idx: number) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start">
                      <span className="mr-2 text-[var(--cedar-primary)] font-bold">•</span>
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Training Phases - 🆕 JAN 2025: Accordion-style expandable */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Training Phases</h3>
              <button
                onClick={() => {
                  // Expand/collapse all
                  if (expandedPhases.size === framework.phases.length) {
                    setExpandedPhases(new Set());
                  } else {
                    setExpandedPhases(new Set(framework.phases.map((_, i) => i)));
                  }
                }}
                className="text-xs text-[var(--cedar-primary)] hover:text-[var(--cedar-dark)] font-medium"
              >
                {expandedPhases.size === framework.phases.length ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
            <div className="space-y-3">
              {framework.phases.map((phase, index) => {
                const isExpanded = expandedPhases.has(index);
                const isCurrent = index === framework.currentPhase;
                const isCompleted = index < framework.currentPhase;
                
                return (
                  <div
                    key={index}
                    className={`border rounded-lg overflow-hidden transition-all ${
                      isCurrent
                        ? 'border-[var(--cedar-primary)] bg-[var(--cedar-subtle)]'
                        : isCompleted
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    {/* Phase Header - Clickable */}
                    <button
                      onClick={() => togglePhase(index)}
                      className="w-full p-4 flex items-center justify-between hover:bg-opacity-80 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isCurrent ? 'bg-[var(--cedar-primary)] text-white' :
                          isCompleted ? 'bg-green-500 text-white' :
                          'bg-gray-300 text-gray-700'
                        }`}>
                          {index + 1}
                        </div>
                      <div className="text-left">
                        <h4 className="font-medium text-gray-900">{phase.name}</h4>
                        <p className="text-xs text-gray-600">{phase.durationWeeks} weeks</p>
                      </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isCurrent && (
                          <span className="bg-[var(--cedar-subtle)] text-[var(--cedar-primary)] text-xs px-2 py-1 rounded-full font-medium border border-[var(--cedar-primary)]">
                            Current
                          </span>
                        )}
                        {isCompleted && (
                          <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                            ✓ Complete
                          </span>
                        )}
                        <svg
                          className={`w-5 h-5 text-gray-600 transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Phase Details - Expandable */}
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-4 animate-fadeIn">
                        <p className="text-sm text-gray-700">{phase.description}</p>
                        
                        {/* 🆕 JAN 19, 2026: Vertical Stacked Sections (No Two-Column) */}
                        <div className="space-y-4 text-sm">
                          {/* Key Workouts */}
                          <div className="bg-white bg-opacity-50 p-3 rounded border border-gray-200">
                            <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                              <svg className="w-4 h-4 mr-1 text-[var(--cedar-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Key Workouts
                            </h5>
                            {phase.keyWorkouts && Array.isArray(phase.keyWorkouts) && phase.keyWorkouts.length > 0 ? (
                              <ul className="text-gray-700 space-y-2">
                                {phase.keyWorkouts.map((workout, i) => (
                                  <li key={i} className="flex items-start">
                                    <span className="mr-2 text-[var(--cedar-primary)] font-bold">•</span>
                                    <span>{workout}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-gray-400 italic">
                                {phase.description ? 'See phase description for workout types' : 'No key workouts specified'}
                              </p>
                            )}
                          </div>
                          
                          {/* Success Criteria */}
                          <div className="bg-white bg-opacity-50 p-3 rounded border border-gray-200">
                            <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                              <svg className="w-4 h-4 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Success Criteria
                            </h5>
                            {phase.successCriteria && Array.isArray(phase.successCriteria) && phase.successCriteria.length > 0 ? (
                              <ul className="text-gray-700 space-y-1">
                                {phase.successCriteria.map((criteria, i) => (
                                  <li key={i} className="flex items-start">
                                    <span className="mr-2 text-green-600">•</span>
                                    {criteria}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-gray-400 italic">No success criteria defined</p>
                            )}
                          </div>
                          
                          {/* 📊 Progressive Metric Targets (Nov 2025) */}
                          {phase.target_metrics && (
                            <div className="p-3 bg-[var(--cedar-subtle)] rounded border border-[var(--cedar-primary)]">
                              <h5 className="font-semibold text-sm text-[var(--cedar-dark)] mb-2 flex items-center">
                                <svg className="w-4 h-4 mr-1 text-[var(--cedar-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00 2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                Phase Targets
                              </h5>
                              
                              {phase.target_metrics.cardiovascular?.target_explanation && (
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-[var(--cedar-dark)]">Cardiovascular:</span>
                                  <p className="text-sm text-gray-700">
                                    {phase.target_metrics.cardiovascular.target_explanation}
                                  </p>
                                </div>
                              )}
                              
                              {phase.target_metrics.performance?.target_explanation && (
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-[var(--cedar-dark)]">Performance:</span>
                                  <p className="text-sm text-gray-700">
                                    {phase.target_metrics.performance.target_explanation}
                                  </p>
                                </div>
                              )}
                              
                              {phase.target_metrics.strength?.target_explanation && (
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-[var(--cedar-dark)]">Strength:</span>
                                  <p className="text-sm text-gray-700">
                                    {phase.target_metrics.strength.target_explanation}
                                  </p>
                                </div>
                              )}
                              
                              {phase.target_metrics.volume && (
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-[var(--cedar-dark)]">Volume:</span>
                                  <p className="text-sm text-gray-700">
                                    {phase.target_metrics.volume.weekly_runs && `${phase.target_metrics.volume.weekly_runs} runs/week`}
                                    {phase.target_metrics.volume.weekly_distance_km && `, ${phase.target_metrics.volume.weekly_distance_km}km total`}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Research Backing */}
                          {phase.researchBacking && (
                            <div className="p-3 bg-white bg-opacity-50 rounded border border-gray-200">
                              <h5 className="font-medium text-gray-800 text-sm mb-1 flex items-center">
                                <svg className="w-4 h-4 mr-1 text-[var(--cedar-accent-sky)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                Research Backing
                              </h5>
                              <p className="text-sm text-gray-700">{phase.researchBacking}</p>
                            </div>
                          )}
                          
                          {/* 🆕 JAN 19, 2026: Supporting Work Guidance */}
                          {phase.supporting_work_guidance && Object.keys(phase.supporting_work_guidance).length > 0 && (
                            <div className="p-3 bg-gray-50 rounded border border-gray-200">
                              <h5 className="font-semibold text-sm text-gray-900 mb-2">Supporting Work Guidance</h5>
                              <div className="space-y-2">
                                {Object.entries(phase.supporting_work_guidance).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="text-xs font-medium text-gray-700 capitalize">
                                      {key.replace(/_/g, ' ')}:
                                    </span>
                                    <p className="text-sm text-gray-600 mt-0.5">{value as string}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Key Milestones - 🎯 JAN 2026: Redesigned to match GoalDetail page styling */}
          {framework.milestones && Array.isArray(framework.milestones) && framework.milestones.length > 0 && (
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Milestones</h3>
              <div className="space-y-3">
                {framework.milestones
                  .sort((a, b) => (a.targetWeek || 0) - (b.targetWeek || 0))
                  .map((milestone) => (
                    <div 
                      key={milestone.id}
                      className="flex items-stretch rounded-lg border border-gray-200 overflow-hidden hover:border-[var(--cedar-primary)] transition-colors"
                    >
                      {/* Left: Green square with Week X */}
                      <div className="bg-[var(--cedar-primary)] flex items-center justify-center px-4 py-3 min-w-[80px]">
                        <span className="text-white font-bold text-sm text-center leading-tight">
                          Week<br />{milestone.targetWeek || '?'}
                        </span>
                      </div>
                      
                      {/* Middle: Milestone content */}
                      <div className="flex-1 p-3 bg-white">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-3">
                            <span className="text-sm font-medium text-gray-900 block mb-1">
                              {milestone.title || 'Milestone'}
                            </span>
                            
                            {/* Success Criteria (if available) */}
                            {milestone.success_criteria && milestone.success_criteria.length > 0 && (
                              <ul className="mt-1 space-y-0.5">
                                {milestone.success_criteria.slice(0, 2).map((criterion, idx) => (
                                  <li key={idx} className="text-xs text-gray-600 flex items-start">
                                    <span className="mr-1 text-[var(--cedar-primary)]">•</span>
                                    {criterion}
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {/* Milestone Type & Assessment Method */}
                            {(milestone.type || milestone.assessmentMethod) && (
                              <div className="mt-2 space-y-0.5">
                                {milestone.type && (
                                  <p className="text-xs text-gray-500">
                                    <span className="font-medium">Type:</span> {milestone.type}
                                  </p>
                                )}
                                {milestone.assessmentMethod && (
                                  <p className="text-xs text-gray-500">
                                    <span className="font-medium">Assessment:</span> {milestone.assessmentMethod.replace(/_/g, ' ')}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Right: Checkbox */}
                          <div 
                            className={`w-7 h-7 rounded-md flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                              milestone.achieved 
                                ? 'bg-green-500' 
                                : 'border-2 border-gray-300'
                            }`}
                          >
                            {milestone.achieved && (
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        
                        {/* Priority badge (if needed) */}
                        {milestone.importance && (
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs ${
                            milestone.importance === 'high' ? 'bg-red-100 text-red-700' :
                            milestone.importance === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {milestone.importance} priority
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* User Adaptations */}
          {framework.userAdaptations && framework.userAdaptations.length > 0 && (
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Adaptations</h3>
              <div className="bg-[var(--cedar-subtle)] p-4 rounded-lg border border-[var(--cedar-primary)]">
                <ul className="text-sm text-gray-700 space-y-2">
                  {framework.userAdaptations.map((adaptation, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[var(--cedar-primary)] mr-2 font-bold">•</span>
                      <span>{adaptation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 🆕 MAR 2026: Research Backing — shows sources and framework names from research event */}
          {(researchApps.length > 0 || researchFrameworkNames.length > 0) && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Research Backing
                </h3>
                <div className="flex items-center gap-2">
                  {researchApps.length > 0 && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      {researchApps.length} {researchApps.length === 1 ? 'Source' : 'Sources'}
                    </span>
                  )}
                  {researchFrameworkNames.length > 0 && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      {researchFrameworkNames.length} {researchFrameworkNames.length === 1 ? 'Framework' : 'Frameworks'}
                    </span>
                  )}
                </div>
              </div>

              {/* Framework Names */}
              {researchFrameworkNames.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Training Frameworks Used</h4>
                  <div className="flex flex-wrap gap-2">
                    {researchFrameworkNames.map((name, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium text-blue-800">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Research Sources */}
              {researchApps.length > 0 && (
                <div className="space-y-3">
                  {researchApps.map((app, index) => (
                    <div key={app.source_id || index} className="border-l-4 border-green-400 pl-4 py-2">
                      {app.source_metadata?.title && (
                        <p className="text-xs text-gray-500 mb-1">
                          {app.source_metadata.authors?.slice(0, 2).join(', ')}{app.source_metadata.authors && app.source_metadata.authors.length > 2 ? ' et al.' : ''}
                          {app.source_metadata.year ? ` (${app.source_metadata.year})` : ''}
                          {app.source_metadata.title ? ` — ${app.source_metadata.title}` : ''}
                        </p>
                      )}
                      <p className="text-sm text-gray-800">{app.cited_finding}</p>
                      {app.applied_to && (
                        <p className="text-xs text-gray-500 mt-1">
                          Applied to: {app.applied_to.replace(/_/g, ' ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Legacy Research Citations (fallback) */}
          {researchApps.length === 0 && framework.researchCitations && framework.researchCitations.length > 0 && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Research Citations</h3>
              <div className="space-y-3">
                {framework.researchCitations.map((citation, index) => (
                  <div key={citation.researchId} className="border-l-4 border-green-400 pl-4 py-2">
                    <p className="text-sm text-gray-800 font-medium">{citation.application}</p>
                    {citation.phaseRelevance && citation.phaseRelevance.length > 0 && (
                      <p className="text-xs text-gray-600 mt-1">
                        Relevant to: {citation.phaseRelevance.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-end">
            <button
              onClick={onClose}
              className="bg-[var(--cedar-primary)] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[var(--cedar-dark)] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
